import AccountVerifyEmailChange from "@/pages/AccountVerifyEmailChange";
import "@/App.css";
import { BrowserRouter, Navigate, Routes, Route } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider } from "@/context/AuthContext";
import { CustomerAuthProvider } from "@/context/CustomerAuthContext";
import { SecureMacProAccountProvider } from "@/context/SecureMacProAccountContext";
import SiteLayout from "@/components/SiteLayout";
import ProtectedRoute from "@/components/ProtectedRoute";
import ScrollToTop from "@/components/ScrollToTop";

import Home from "@/pages/Home";
import Platform from "@/pages/Platform";
import Security from "@/pages/Security";
import Deployment from "@/pages/Deployment";
import UseCases from "@/pages/UseCases";
import Investors from "@/pages/Investors";
import AdminLogin from "@/pages/AdminLogin";
import AdminLeads from "@/pages/AdminLeads";
import HaloFoundingCampaign from "@/pages/HaloFoundingCampaign";
import HaloProductPage from "@/pages/HaloProductPage";
import HaloFortressProductPage from "@/pages/HaloFortressProductPage";
import SecureMacProductPage from "@/pages/SecureMacProductPage";
import SecureMacProProductPage from "@/pages/SecureMacProProductPage";
import SecureAIProductPage from "@/pages/SecureAIProductPage";
import SecureAIEnterprisePage from "@/pages/SecureAIEnterprisePage";
import HaloPrivacy from "@/pages/HaloPrivacy";
import HaloTerms from "@/pages/HaloTerms";
import HaloSupport from "@/pages/HaloSupport";
import HaloAccount from "@/pages/HaloAccount";
import HaloAccountDeletion from "@/pages/HaloAccountDeletion";
import HaloAccountDeletionRequest from "@/pages/HaloAccountDeletionRequest";
import HaloEducationArticle from "@/pages/HaloEducationArticle";
import HaloLearnHub from "@/pages/HaloLearnHub";
import Account from "@/pages/Account";
import ProductPage from "@/pages/ProductPage";
import SheilaProductPage from "@/pages/SheilaProductPage";
import QuantumCubeDB from "@/pages/QuantumCubeDB";
import Release from "@/pages/Release";
import Evidence from "@/pages/Evidence";
import Privacy from "@/pages/Privacy";
import Terms from "@/pages/Terms";
import Support from "@/pages/Support";
import SecureMacPrivacy from "@/pages/SecureMacPrivacy";
import SecureMacProTerms from "@/pages/SecureMacProTerms";
import SecureMacRefunds from "@/pages/SecureMacRefunds";
import SecureMacSupport from "@/pages/SecureMacSupport";
import SecureMacAccountDeletion from "@/pages/SecureMacAccountDeletion";
import SecureMacProVerifyEmail from "@/pages/SecureMacProVerifyEmail";
import SecureMacProResetPassword from "@/pages/SecureMacProResetPassword";
import NotFound from "@/pages/NotFound";

export default function App() {
  return (
    <div className="App">
      <AuthProvider>
      <CustomerAuthProvider>
      <SecureMacProAccountProvider>
        <BrowserRouter>
          <ScrollToTop />
          <Routes>
            <Route
              path="/"
              element={
                <SiteLayout>
                  <Home />
                </SiteLayout>
              }
            />
            <Route
              path="/platform"
              element={
                <SiteLayout>
                  <Platform />
                </SiteLayout>
              }
            />
            <Route
              path="/security"
              element={
                <SiteLayout>
                  <Security />
                </SiteLayout>
              }
            />
            <Route
              path="/deployment"
              element={
                <SiteLayout>
                  <Deployment />
                </SiteLayout>
              }
            />
            <Route
              path="/use-cases"
              element={
                <SiteLayout>
                  <UseCases />
                </SiteLayout>
              }
            />
            <Route
              path="/investors"
              element={
                <SiteLayout>
                  <Investors />
                </SiteLayout>
              }
            />
            <Route
              path="/halo/founding-campaign"
              element={
                <SiteLayout>
                  <HaloFoundingCampaign />
                </SiteLayout>
              }
            />
            <Route path="/halo/funding" element={<Navigate to="/halo/founding-campaign" replace />} />
            <Route path="/funding" element={<Navigate to="/halo/founding-campaign" replace />} />
            <Route path="/founding-members" element={<Navigate to="/halo/founding-campaign" replace />} />
            <Route path="/halo" element={<SiteLayout><HaloProductPage /></SiteLayout>} />
            <Route path="/halo-fortress" element={<SiteLayout><HaloFortressProductPage edition="base" /></SiteLayout>} />
            <Route path="/halo-fortress-pro" element={<SiteLayout><HaloFortressProductPage edition="pro" /></SiteLayout>} />
            <Route path="/secureshield" element={<SiteLayout><SecureMacProductPage /></SiteLayout>} />
            <Route path="/securemac" element={<SiteLayout><SecureMacProductPage /></SiteLayout>} />
            <Route path="/secureshield-pro" element={<SiteLayout><SecureMacProProductPage /></SiteLayout>} />
            <Route path="/securemac-pro" element={<SiteLayout><SecureMacProProductPage /></SiteLayout>} />
            <Route path="/account" element={<SiteLayout><Account /></SiteLayout>} />
            <Route path="/account/profile" element={<SiteLayout><Account /></SiteLayout>} />
            <Route path="/account/approve-device" element={<SiteLayout><Account /></SiteLayout>} />
            <Route path="/account/link-store" element={<SiteLayout><Account /></SiteLayout>} />
            <Route path="/account/complete-profile" element={<SiteLayout><Account /></SiteLayout>} />
            <Route path="/account/verify-email" element={<SiteLayout><SecureMacProVerifyEmail /></SiteLayout>} />
            <Route path="/account/verify-email-change" element={<SiteLayout><AccountVerifyEmailChange /></SiteLayout>} />
            <Route path="/account/reset-password" element={<SiteLayout><SecureMacProResetPassword /></SiteLayout>} />
            <Route path="/secureai" element={<SiteLayout><SecureAIProductPage /></SiteLayout>} />
            <Route path="/secureai-enterprise" element={<SiteLayout><SecureAIEnterprisePage /></SiteLayout>} />
            <Route path="/halo/start" element={<Navigate to="/halo" replace />} />
            <Route path="/halo/privacy" element={<SiteLayout><HaloPrivacy /></SiteLayout>} />
            <Route path="/halo/terms" element={<SiteLayout><HaloTerms /></SiteLayout>} />
            <Route path="/halo/support" element={<SiteLayout><HaloSupport /></SiteLayout>} />
            <Route path="/halo/account" element={<SiteLayout><HaloAccount /></SiteLayout>} />
            <Route path="/halo/account-deletion" element={<SiteLayout><HaloAccountDeletion /></SiteLayout>} />
            <Route path="/halo/account/delete" element={<SiteLayout><HaloAccountDeletionRequest /></SiteLayout>} />
            <Route path="/halo/learn" element={<SiteLayout><HaloLearnHub /></SiteLayout>} />
            <Route path="/halo/learn/what-is-quantumcloak" element={<SiteLayout><HaloEducationArticle slug="what-is-quantumcloak" /></SiteLayout>} />
            <Route path="/halo/learn/why-post-quantum-protection-matters" element={<SiteLayout><HaloEducationArticle slug="why-post-quantum-protection-matters" /></SiteLayout>} />
            <Route path="/quantumcloak" element={<SiteLayout><ProductPage product="quantumcloak" /></SiteLayout>} />
            <Route path="/quantumcubedb" element={<SiteLayout><QuantumCubeDB /></SiteLayout>} />
            <Route path="/fpga-acceleration" element={<SiteLayout><ProductPage product="fpga-acceleration" /></SiteLayout>} />
            <Route path="/sheila-ai" element={<SiteLayout><SheilaProductPage /></SiteLayout>} />
            <Route path="/release/v9-0-1" element={<SiteLayout><Release /></SiteLayout>} />
            <Route path="/evidence" element={<SiteLayout><Evidence /></SiteLayout>} />
            <Route path="/privacy" element={<SiteLayout><Privacy /></SiteLayout>} />
            <Route path="/terms" element={<SiteLayout><Terms /></SiteLayout>} />
            <Route path="/support" element={<SiteLayout><Support /></SiteLayout>} />
            <Route path="/secureshield/privacy" element={<SiteLayout><SecureMacPrivacy /></SiteLayout>} />
            <Route path="/securemac/privacy" element={<SiteLayout><SecureMacPrivacy /></SiteLayout>} />
            <Route path="/secureshield/pro-terms" element={<SiteLayout><SecureMacProTerms /></SiteLayout>} />
            <Route path="/securemac/pro-terms" element={<SiteLayout><SecureMacProTerms /></SiteLayout>} />
            <Route path="/secureshield/refunds" element={<SiteLayout><SecureMacRefunds /></SiteLayout>} />
            <Route path="/securemac/refunds" element={<SiteLayout><SecureMacRefunds /></SiteLayout>} />
            <Route path="/secureshield/support" element={<SiteLayout><SecureMacSupport /></SiteLayout>} />
            <Route path="/securemac/support" element={<SiteLayout><SecureMacSupport /></SiteLayout>} />
            <Route path="/secureshield/account-deletion" element={<SiteLayout><SecureMacAccountDeletion /></SiteLayout>} />
            <Route path="/securemac/account-deletion" element={<SiteLayout><SecureMacAccountDeletion /></SiteLayout>} />
            <Route path="/security-trust" element={<Navigate to="/security" replace />} />

            {/* Admin */}
            <Route path="/admin/login" element={<AdminLogin />} />
            <Route
              path="/admin/leads"
              element={
                <ProtectedRoute>
                  <AdminLeads />
                </ProtectedRoute>
              }
            />

            <Route
              path="*"
              element={
                <SiteLayout>
                  <NotFound />
                </SiteLayout>
              }
            />
          </Routes>
        </BrowserRouter>
        <Toaster
          position="bottom-right"
          toastOptions={{
            style: {
              borderRadius: 0,
              border: "1px solid #E4E4E7",
              fontFamily: "Satoshi, sans-serif",
            },
          }}
        />
      </SecureMacProAccountProvider>
      </CustomerAuthProvider>
      </AuthProvider>
    </div>
  );
}
